/**
 * Admin QC Settings Page
 * Allows admins to configure QC workflow settings including SLA threshold
 * P1 QC Dashboard - Admin configurable SLA threshold
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Clock, Save, RefreshCw, Shield, Info, CheckCircle, AlertTriangle, Timer } from 'lucide-react';
import { formatDateTimeShortET } from '../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const AdminQCSettings = () => {
  const [slaThresholdHours, setSlaThresholdHours] = useState(4);
  const [qcEnabled, setQcEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalValue, setOriginalValue] = useState(4);
  const [rejectionCategories, setRejectionCategories] = useState([]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch QC config
      const response = await fetch(`${API}/qc/config`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch QC settings');
      }

      const data = await response.json();
      setSlaThresholdHours(data.sla_threshold_hours || 4);
      setOriginalValue(data.sla_threshold_hours || 4);
      setQcEnabled(data.qc_enabled || false);
      setRejectionCategories(data.rejection_categories || []);
      setHasChanges(false);
      
      // Try to get last updated info from settings collection
      try {
        const settingsResponse = await fetch(`${API}/admin/settings`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (settingsResponse.ok) {
          const settingsData = await settingsResponse.json();
          const qcSetting = settingsData.find?.(s => s.key === 'qc_sla_threshold_hours');
          if (qcSetting) {
            setLastUpdated(qcSetting.updated_at);
            setUpdatedBy(qcSetting.updated_by);
          }
        }
      } catch {
        // Settings endpoint may not exist, that's ok
      }
    } catch (error) {
      console.error('Error fetching QC settings:', error);
      toast.error('Failed to load QC settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSliderChange = (value) => {
    const newValue = value[0];
    setSlaThresholdHours(newValue);
    setHasChanges(newValue !== originalValue);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/qc/config/sla-threshold`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ threshold_hours: slaThresholdHours })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save settings');
      }

      const data = await response.json();
      setOriginalValue(data.sla_threshold_hours);
      setHasChanges(false);
      setLastUpdated(new Date().toISOString());
      
      toast.success(`SLA threshold updated to ${slaThresholdHours} hours`, {
        description: 'Changes take effect immediately for all QC users'
      });
    } catch (error) {
      console.error('Error saving QC settings:', error);
      toast.error(error.message || 'Failed to save QC settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSlaThresholdHours(originalValue);
    setHasChanges(false);
  };

  const formatLastUpdated = (dateString) => {
    if (!dateString) return 'Never';
    return formatDateTimeShortET(dateString);
  };

  // Get SLA color preview based on threshold
  const getSLAPreview = () => {
    return [
      { range: `< ${Math.floor(slaThresholdHours / 2)}h`, color: 'bg-gray-100 text-gray-700', label: 'Neutral' },
      { range: `${Math.floor(slaThresholdHours / 2)}h - ${slaThresholdHours}h`, color: 'bg-amber-100 text-amber-700', label: 'Warning' },
      { range: `> ${slaThresholdHours}h`, color: 'bg-red-100 text-red-700', label: 'Breach' },
      { range: `> ${slaThresholdHours * 2}h`, color: 'bg-red-200 text-red-800 animate-pulse', label: 'Critical' }
    ];
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-green-500/20 rounded-lg">
          <CheckCircle className="h-6 w-6 text-green-500" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-100">QC Settings</h1>
          <p className="text-slate-400">Configure Quality Control workflow parameters</p>
        </div>
      </div>

      {/* QC Status Card */}
      <Card className={qcEnabled ? 'border-green-200 bg-green-50/50' : 'border-amber-200 bg-amber-50/50'}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            {qcEnabled ? (
              <CheckCircle className="h-6 w-6 text-green-600" />
            ) : (
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            )}
            <div>
              <p className="font-medium text-slate-900">
                QC Workflow is {qcEnabled ? 'Enabled' : 'Disabled'}
              </p>
              <p className="text-sm text-slate-600">
                {qcEnabled 
                  ? 'Orders from users with requires_qc=true will be routed to QC review.'
                  : 'QC workflow is currently disabled. Enable via feature flag.'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SLA Threshold Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Timer className="h-5 w-5 text-blue-500" />
            SLA Threshold
          </CardTitle>
          <CardDescription>
            Orders in QC review exceeding this threshold will be flagged as SLA breaches.
            This affects the QC Dashboard metrics and visual indicators.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Threshold Slider */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">SLA Threshold Duration</Label>
              <span className="text-2xl font-bold text-blue-600">
                {slaThresholdHours} hours
              </span>
            </div>
            
            <Slider
              value={[slaThresholdHours]}
              onValueChange={handleSliderChange}
              min={1}
              max={24}
              step={1}
              className="w-full"
            />
            
            <div className="flex justify-between text-sm text-slate-500">
              <span>1h (strict)</span>
              <span>4h (default)</span>
              <span>8h (relaxed)</span>
              <span>24h (max)</span>
            </div>
          </div>

          {/* SLA Color Preview */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Time in QC Badge Preview:</Label>
            <div className="flex flex-wrap gap-2">
              {getSLAPreview().map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${item.color}`}>
                    {item.range}
                  </span>
                  <span className="text-xs text-slate-500">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
            <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">How SLA Threshold Works:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Orders waiting longer than threshold show red "breach" badge</li>
                <li>Orders 2x over threshold show pulsing critical badge</li>
                <li>SLA Breaches KPI counts orders exceeding this threshold</li>
                <li>My Workspace shows personal SLA breach count</li>
              </ul>
            </div>
          </div>

          {/* Last Updated Info */}
          {lastUpdated && (
            <div className="text-sm text-slate-500 border-t pt-4">
              Last updated: {formatLastUpdated(lastUpdated)}
              {updatedBy && ` by ${updatedBy}`}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="flex-1"
            >
              {saving ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
            
            {hasChanges && (
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={saving}
              >
                Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Rejection Categories Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-purple-500" />
            Rejection Categories
          </CardTitle>
          <CardDescription>
            These categories are used when QC Specialists reject orders for revision.
            Categories are defined in the backend and cannot be changed from UI.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {rejectionCategories.map((cat, idx) => (
              <span 
                key={idx} 
                className="px-3 py-1.5 text-sm bg-purple-50 text-purple-700 border border-purple-200 rounded-full"
              >
                {cat.label}
              </span>
            ))}
          </div>
          {rejectionCategories.length === 0 && (
            <p className="text-sm text-slate-500">No categories loaded</p>
          )}
        </CardContent>
      </Card>

      {/* Info Note */}
      <Card className="bg-slate-50">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Shield className="h-5 w-5 text-slate-400 flex-shrink-0" />
            <div className="text-sm text-slate-600">
              <p className="font-medium text-slate-700 mb-1">Configuration Note</p>
              <p>
                SLA threshold changes take effect immediately for all QC Specialists.
                Consider your team's capacity and order complexity when setting thresholds.
                Shorter thresholds encourage faster review but may increase pressure on reviewers.
                All configuration changes are logged for audit purposes.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminQCSettings;
